import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, ChevronDown, ExternalLink } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'pending_payment', label: 'In Attesa Pagamento', color: 'bg-gray-100 text-gray-700' },
  { value: 'new', label: 'Nuovo', color: 'bg-blue-100 text-blue-700' },
  { value: 'ordered_from_supplier', label: 'Ordinato al Fornitore', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'shipped', label: 'Spedito', color: 'bg-purple-100 text-purple-700' },
  { value: 'completed', label: 'Completato', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Annullato', color: 'bg-red-100 text-red-700' },
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    fetchOrders();
  }, [statusFilter, page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '10' });
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API}/admin/orders?${params}`);
      setOrders(response.data.orders);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Errore nel caricamento ordini');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status: newStatus });
      toast.success('Stato ordine aggiornato');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento stato');
    }
  };

  const viewOrderDetail = async (orderId) => {
    try {
      const response = await axios.get(`${API}/admin/orders/${orderId}`);
      setSelectedOrder(response.data);
      setDetailOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento dettagli');
    }
  };

  const getStatusBadge = (status) => {
    const opt = statusOptions.find(s => s.value === status);
    return <Badge className={opt?.color || 'bg-gray-100 text-gray-700'}>{opt?.label || status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-serif">Ordini</h1>
          <p className="text-muted-foreground mt-1">Gestisci gli ordini dei clienti</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={(val) => { setStatusFilter(val); setPage(1); }}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtra per stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {statusOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-4 font-medium">Ordine</th>
                <th className="text-left p-4 font-medium">Cliente</th>
                <th className="text-left p-4 font-medium">Totale</th>
                <th className="text-left p-4 font-medium">Margine</th>
                <th className="text-left p-4 font-medium">Stato</th>
                <th className="text-left p-4 font-medium">Data</th>
                <th className="text-left p-4 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="border-t border-border">
                    <td colSpan={7} className="p-4">
                      <div className="h-12 bg-muted rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    Nessun ordine trovato.
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className="border-t border-border hover:bg-muted/20" data-testid={`order-row-${order.id}`}>
                    <td className="p-4">
                      <p className="font-mono font-medium">#{order.id.slice(0, 8).toUpperCase()}</p>
                    </td>
                    <td className="p-4">
                      <p className="font-medium">{order.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                    </td>
                    <td className="p-4 font-bold">
                      €{order.total?.toFixed(2)}
                    </td>
                    <td className="p-4">
                      <span className="text-green-600 font-medium">
                        €{order.margin?.toFixed(2)}
                      </span>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString('it-IT')}
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => viewOrderDetail(order.id)}
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Select
                          value={order.status}
                          onValueChange={(val) => updateStatus(order.id, val)}
                        >
                          <SelectTrigger className="w-auto h-8 px-2">
                            <ChevronDown className="h-4 w-4" />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-border">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Precedente
            </Button>
            <span className="flex items-center px-4 text-sm text-muted-foreground">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Successivo
            </Button>
          </div>
        )}
      </div>

      {/* Order Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio Ordine #{selectedOrder?.id?.slice(0, 8).toUpperCase()}</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6 py-4">
              {/* Status */}
              <div className="flex items-center justify-between">
                <span className="font-medium">Stato:</span>
                <Select
                  value={selectedOrder.status}
                  onValueChange={(val) => updateStatus(selectedOrder.id, val)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Customer Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Cliente</h4>
                <p>{selectedOrder.customer_name}</p>
                <p className="text-sm text-muted-foreground">{selectedOrder.customer_email}</p>
                {selectedOrder.customer_phone && (
                  <p className="text-sm text-muted-foreground">{selectedOrder.customer_phone}</p>
                )}
              </div>

              {/* Shipping Address */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="font-semibold mb-2">Indirizzo Spedizione</h4>
                <p>{selectedOrder.shipping_address}</p>
                <p>{selectedOrder.shipping_postal_code} {selectedOrder.shipping_city}</p>
                <p>{selectedOrder.shipping_country}</p>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-semibold mb-2">Prodotti</h4>
                <div className="space-y-2">
                  {(!selectedOrder.items || selectedOrder.items.length === 0) ? (
                    <p className="text-muted-foreground text-sm p-3 bg-muted/30 rounded-lg">
                      Nessun prodotto in questo ordine (ordine test o vuoto)
                    </p>
                  ) : (
                    selectedOrder.items?.map((item, i) => (
                      <div key={i} className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                        <div className="flex gap-3 flex-1">
                          {item.image && (
                            <img 
                              src={item.image} 
                              alt={item.title} 
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <p className="font-medium">{item.title}</p>
                            <p className="text-sm text-muted-foreground">
                              Qty: {item.quantity} × €{item.price?.toFixed(2) || item.unit_price?.toFixed(2)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Costo fornitore: €{item.supplier_cost?.toFixed(2) || '0.00'}
                            </p>
                            <div className="flex gap-3 mt-2">
                              {item.product_id && (
                                <a 
                                  href={`https://qantri.store/product/${item.product_id}`}
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  Vedi su QANTRI
                                </a>
                              )}
                              {item.supplier_url && (
                                <a 
                                  href={item.supplier_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 hover:underline"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  CJDropshipping
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">€{item.total?.toFixed(2)}</p>
                          <p className="text-xs text-green-600">
                            +€{(((item.price || item.unit_price || 0) - (item.supplier_cost || 0)) * item.quantity).toFixed(2)} margine
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Totals */}
              <div className="border-t border-border pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Totale Ordine:</span>
                  <span className="font-bold">€{selectedOrder.total?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-green-600">
                  <span>Margine Guadagno:</span>
                  <span className="font-bold">€{selectedOrder.margin?.toFixed(2)}</span>
                </div>
              </div>

              {selectedOrder.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-1">Note Ordine</h4>
                  <p className="text-sm">{selectedOrder.notes}</p>
                </div>
              )}

              {/* CJ Dropshipping Quick Action */}
              {selectedOrder.status === 'paid' && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-emerald-800">Evadi su CJ Dropshipping</h4>
                  <p className="text-sm text-emerald-700 mb-3">
                    Questo ordine è stato inviato automaticamente a CJ. Clicca per pagare e completare la spedizione.
                  </p>
                  <a 
                    href="https://app.cjdropshipping.com/orders/list" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Vai ai tuoi ordini CJ
                  </a>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrders;
